/**
 * Páginas legales mínimas (RGPD + Términos).
 *
 * Esqueleto base — el usuario debe revisar con asesoría jurídica antes
 * de abrir beta pública. Las URLs `/privacidad` y `/terminos` se enlazan
 * desde los formularios públicos (consentimiento implícito al enviar).
 */
import GlobalHeader from "../components/layout/GlobalHeader";

function LegalShell({ title, lastUpdated, children, testId }) {
  return (
    <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)]" data-testid={testId}>
      <GlobalHeader />
      <main className="max-w-3xl mx-auto px-4 py-12">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">AG-Ecosystem</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">{title}</h1>
        <p className="text-xs text-[var(--ag-text-dim)] mt-2">Última actualización: {lastUpdated}</p>
        <div className="prose prose-invert max-w-none mt-8 text-sm leading-relaxed space-y-4">
          {children}
        </div>
        <div className="mt-12 pt-6 border-t border-[var(--ag-border)] text-xs text-[var(--ag-text-dim)]">
          <p>Si tienes dudas: <a href="mailto:hola@ag-ecosystem.com" className="text-[var(--ag-gold)] hover:underline">hola@ag-ecosystem.com</a></p>
        </div>
      </main>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Política de privacidad" lastUpdated="15 de febrero de 2026" testId="page-privacy">
      <h2 className="text-lg font-semibold mt-6">1. Responsable del tratamiento</h2>
      <p>AG-Ecosystem (en adelante, &quot;AutoGlow&quot;) es el responsable del tratamiento de los datos personales que nos facilitas mediante los formularios públicos de esta plataforma.</p>

      <h2 className="text-lg font-semibold mt-6">2. Qué datos recogemos</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Formulario &quot;Solicitar cita&quot;</b>: nombre, teléfono, email, servicio de interés y mensaje libre. Los datos se entregan al negocio destinatario.</li>
        <li><b>Formulario &quot;Reclamar perfil&quot;</b>: email, nombre del contacto, nota. Sirve para validar la titularidad del negocio.</li>
        <li><b>Auto-alta de negocio</b>: nombre del negocio, sectores, ciudad, dirección, email, teléfono, NIF (opcional), descripción.</li>
        <li><b>Datos de navegación</b>: IP, User-Agent y session_id se almacenan de forma anonimizada (hash SHA256 con sal) para métricas agregadas. <b>No identifican personalmente al usuario.</b></li>
      </ul>

      <h2 className="text-lg font-semibold mt-6">3. Para qué los usamos</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Entregar tu consulta al negocio que has contactado.</li>
        <li>Crear o reclamar tu perfil de negocio en el directorio.</li>
        <li>Enviarte emails transaccionales (confirmación, recordatorios, factura).</li>
        <li>Métricas anonimizadas de funcionamiento del marketplace (cumplimiento RGPD).</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6">4. Base legal</h2>
      <p>El tratamiento se basa en tu consentimiento expreso (al enviar cualquier formulario) y, cuando aplica, en la ejecución del contrato de servicio (clientes con suscripción activa).</p>

      <h2 className="text-lg font-semibold mt-6">5. Conservación</h2>
      <p>Conservamos tus datos mientras tu cuenta esté activa o exista actividad comercial. Después aplicamos los plazos legales: contables (6 años), fiscales (5 años) y los aplicables a la Ley Antifraude.</p>

      <h2 className="text-lg font-semibold mt-6">6. Tus derechos</h2>
      <p>Puedes ejercer en cualquier momento tus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a <a href="mailto:hola@ag-ecosystem.com" className="text-[var(--ag-gold)]">hola@ag-ecosystem.com</a>. También puedes reclamar ante la Agencia Española de Protección de Datos (<a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[var(--ag-gold)]">aepd.es</a>).</p>

      <h2 className="text-lg font-semibold mt-6">7. Encargados del tratamiento</h2>
      <p>Trabajamos con proveedores que cumplen RGPD: Resend (envío de emails), Stripe (pagos), Cloudflare (anti-spam Turnstile), MongoDB Atlas / hosting Emergent (almacenamiento). Todos firman cláusulas de tratamiento de datos.</p>

      <p className="italic text-xs mt-6 opacity-70">Esta política es un esqueleto base. Antes de abrir la beta pública te recomendamos revisarla con tu asesor jurídico.</p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Términos de servicio" lastUpdated="15 de febrero de 2026" testId="page-terms">
      <h2 className="text-lg font-semibold mt-6">1. Aceptación</h2>
      <p>Al usar AG-Ecosystem (web, marketplace, dashboard de negocio o cualquier formulario público) aceptas estos términos. Si no estás de acuerdo, no uses el servicio.</p>

      <h2 className="text-lg font-semibold mt-6">2. Qué ofrecemos</h2>
      <p>Una plataforma SaaS para profesionales del motor (talleres mecánicos, detailing, chapa y pintura, neumáticos, diagnosis, ADAS, cristalería, ITV, homologaciones, autoescuelas, gestorías, renting, flotas, vehículo industrial y freelancers) que incluye perfil público, marketplace, sistema de reservas, CRM clientes, facturación y analíticas. Modelo Freemium con prueba gratuita de 30 días sin tarjeta de crédito.</p>

      <h2 className="text-lg font-semibold mt-6">3. Trial gratuito y planes</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>El trial dura 30 días naturales desde la activación.</li>
        <li>No solicitamos tarjeta de crédito durante el trial.</li>
        <li>Al final del trial, sin acción tuya, el acceso al panel queda limitado pero <b>conservas todos tus datos</b>.</li>
        <li>Para reactivar basta elegir un plan en <a href="/precios" className="text-[var(--ag-gold)]">/precios</a>.</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6">4. Suscripciones de pago</h2>
      <p>Renovación automática mensual vía Stripe. Puedes cancelar en cualquier momento desde tu dashboard sin penalización; el servicio seguirá activo hasta el final del período facturado.</p>

      <h2 className="text-lg font-semibold mt-6">5. Uso aceptable</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>No puedes usar la plataforma para spam, contenido ilegal o suplantación de identidad.</li>
        <li>Las reclamaciones de perfil deben ser legítimas. Detectamos y baneamos intentos fraudulentos.</li>
        <li>Los datos de leads que recibas son confidenciales — debes tratarlos conforme al RGPD.</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6">6. Disponibilidad</h2>
      <p>Trabajamos para mantener un servicio &gt;99% del tiempo, pero no garantizamos uptime contractual durante la fase beta. Notificaremos las incidencias por email.</p>

      <h2 className="text-lg font-semibold mt-6">7. Cancelación de cuenta</h2>
      <p>Puedes solicitar la eliminación de tu cuenta en cualquier momento escribiendo a <a href="mailto:hola@ag-ecosystem.com" className="text-[var(--ag-gold)]">hola@ag-ecosystem.com</a>. Conservaremos los registros contables el tiempo legalmente exigido.</p>

      <h2 className="text-lg font-semibold mt-6">8. Modificaciones</h2>
      <p>Podemos actualizar estos términos. Te avisaremos por email con al menos 15 días de antelación si los cambios son relevantes.</p>

      <h2 className="text-lg font-semibold mt-6">9. Ley aplicable</h2>
      <p>Estos términos se rigen por la legislación española. Cualquier disputa se someterá a los juzgados de la provincia del domicilio del titular de AutoGlow.</p>

      <p className="italic text-xs mt-6 opacity-70">Este texto es un borrador funcional. Antes de la beta pública revísalo con tu asesor jurídico.</p>
    </LegalShell>
  );
}
